import Box from "@mui/material/Box";

import type { JobylonRole } from "../../api/assignments/jobylon-jobs";
import { JobylonBadge } from "./jobylon-badge";

interface JobylonPresenceMarkProps {
	/** Every Jobylon application this customer holds for the person. */
	roles: JobylonRole[];
	/** Rendered height in pixels; the width follows the logo's aspect ratio. */
	height?: number;
}

/** One line per application: the role, and where Jobylon holds them on it. */
const describe = (roles: JobylonRole[]): string =>
	roles
		.map((role) => {
			const where = role.stage ? ` — ${role.stage}` : "";
			const how = role.origin === "applied" ? "applied" : "sourced";
			// Why they were turned down, when the customer said. Without it
			// "Nej-tack" reads as a verdict on the person rather than on one
			// application, and the next role is judged by it.
			const why = role.reason ? `\n    why: ${role.reason}` : "";
			return `${role.title || "Untitled role"}${where} (${how})${why}`;
		})
		.join("\n");

/**
 * Says the customer already has this person in Jobylon, wherever they appear.
 *
 * The role-scoped marks answer "where does this person stand on THIS job".
 * This one answers the question asked when a name turns up somewhere else
 * entirely — a search result, another assignment's queue — which is whether
 * the customer is already talking to them, and so whether approaching them
 * again is a mistake.
 *
 * Muted once every application is closed: rejected or hired is still worth
 * knowing, and stops a second pitch to someone already turned down.
 */
export const JobylonPresenceMark = ({
	roles,
	height = 14,
}: JobylonPresenceMarkProps) => {
	if (roles.length === 0) {
		return null;
	}
	const isSettled = roles.every((role) => role.is_closed);
	// One link is only meaningful when there is one application to open.
	const href = roles.length === 1 ? roles[0].board_url : undefined;

	return (
		<Box
			component="span"
			data-component="jobylon-presence-mark"
			sx={{ display: "inline-flex", verticalAlign: "middle", ml: 0.5 }}
		>
			<JobylonBadge
				height={height}
				href={href || undefined}
				muted={isSettled}
				title={`In Jobylon:\n${describe(roles)}`}
			/>
		</Box>
	);
};

export default JobylonPresenceMark;
